import { logger } from "@/logging";
import { BaseRenderer } from "../video/BaseRenderer";

import { HydraChain } from "./HydraEndpoint";
import { buildHydraFn } from "./nodes/buildHydraFn";
import { buildChainFromGraph } from "./buildChainFromGraph";
import type { GraphIndex } from "@/runtime/GraphIndex";

export class HydraRenderer extends BaseRenderer {

  Hydra: any;
  chain: HydraChain = [];
  hydraInstance: any;

  key: string;
  private graphIndex: GraphIndex | null = null;
  private nodeId: string = '';
  private instancePath: string = '';
  private lastGraphVersion: number = -1;

  constructor(Hydra: any, key: string) {
    super();
    this.Hydra = Hydra;
    this.key = key;
  }

  /** Configure graph-query mode: renderer rebuilds chain from graph on version change */
  setGraphSource(graphIndex: GraphIndex, nodeId: string, instancePath: string): void {
    this.graphIndex = graphIndex;
    this.nodeId = nodeId;
    this.instancePath = instancePath;
    this.lastGraphVersion = -1; // force rebuild on next render
  }


  

  protected initializeRenderer(canvas: HTMLCanvasElement): void {
    this.hydraInstance = new this.Hydra({
      canvas,
      autoLoop: false,
      detectAudio: false,
      enableStreamCapture: false,
      width: canvas.width,
      height: canvas.height,
      numSources: 4,
      numOutputs: 1
    });

    this.renderBuffer();
  }

  private hasSource(): boolean {
    return this.chain.some(m => m && (m.source || m.newSource));
  }

  renderBuffer() {
    if (!this.hydraInstance || !this.chain || !this.hasSource()) return;
    const hydraFn = buildHydraFn(this.hydraInstance, this.chain);
    const buffer = this.hydraInstance.o[0];
    if (hydraFn) {
      try {
        hydraFn.out(buffer);
      } catch (e) {
        logger.error('Hydra renderBuffer error', e);
      }
    }
  }

  setSize(width: number, height: number) {
    try {
      if (this.hydraInstance) {
        this.hydraInstance.resize([width, height]);
      }
    } catch (e) {
      logger.trace('hydra resize error', e, width, height);
    }
  }

  /** Check graph version and rebuild chain if topology changed */
  private updateChainFromGraph(): void {
    if (!this.graphIndex) return;
    const currentVersion = this.graphIndex.version;
    if (currentVersion === this.lastGraphVersion) return;

    this.chain = buildChainFromGraph(this.graphIndex, this.nodeId, this.instancePath);
    // Only cache version if chain is non-empty. An empty chain may mean
    // upstream resources (e.g. video canvas) aren't ready yet — retry next frame.
    if (this.chain.length > 0) {
      this.lastGraphVersion = currentVersion;
    }
    this.renderBuffer();
  }

  protected _render(dt?: number) {
    if (this.graphIndex) {
      this.updateChainFromGraph();
    }
    if (this.hydraInstance) {
      this.hydraInstance.tick(dt);
    }
  }
}